import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause, RotateCcw, Square, CheckCircle2 } from "lucide-react";

// Video player used across Home (intro) and Training Manual modules.
// - If `videoUrl` is provided, renders a real <video> with custom Play/Pause,
//   Rewind (-10s) and Stop controls.
// - If no `videoUrl` is provided (module videos not yet supplied by client),
//   falls back to a simulated placeholder with a play button.
export default function VideoPlaceholder({ moduleId, title, videoUrl, onComplete, completed, testIdPrefix }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  // --- Real video controls ---
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
    } else {
      v.pause();
    }
  };

  const handleRewind = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, v.currentTime - 10);
  };

  const handleStop = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    setPlaying(false);
    setVideoProgress(0);
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setVideoProgress((v.currentTime / v.duration) * 100);
  };

  const handleEnded = () => {
    setPlaying(false);
    onComplete && onComplete();
  };

  if (videoUrl) {
    return (
      <div className="w-full">
        <div className="relative aspect-video w-full max-h-[80vh] rounded-lg overflow-hidden bg-black">
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full h-full object-contain bg-black"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            data-testid={`${testIdPrefix}-video-element-${moduleId}`}
          />
          {completed && (
            <div className="absolute top-3 right-3 bg-emerald-500/90 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Watched
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/60 to-transparent pt-6 pb-3 px-4 space-y-2">
            <div className="h-1 w-full bg-white/25 rounded-full overflow-hidden">
              <div
                className="h-full bg-denso-lightred transition-all duration-150"
                style={{ width: `${videoProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handleRewind}
                data-testid={`${testIdPrefix}-rewind-button-${moduleId}`}
                title="Rewind 10s"
                className="h-9 w-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-200"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={togglePlay}
                data-testid={`${testIdPrefix}-playpause-button-${moduleId}`}
                title={playing ? "Pause" : "Play"}
                className="h-11 w-11 rounded-full bg-denso-red flex items-center justify-center text-white hover:bg-denso-lightred transition-colors duration-200"
              >
                {playing ? <Pause className="h-5 w-5 fill-white" /> : <Play className="h-5 w-5 fill-white ml-0.5" />}
              </button>
              <button
                onClick={handleStop}
                data-testid={`${testIdPrefix}-stop-button-${moduleId}`}
                title="Stop"
                className="h-9 w-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-200"
              >
                <Square className="h-3.5 w-3.5 fill-white" />
              </button>
            </div>
          </div>
        </div>
        {title && <p className="text-xs text-slate-400 mt-2 text-center">{title}</p>}
      </div>
    );
  }

  return <PlaceholderSimulation
    moduleId={moduleId}
    title={title}
    onComplete={onComplete}
    completed={completed}
    testIdPrefix={testIdPrefix}
  />;
}

// Simulated "watch" experience for modules that don't have a real video yet.
function PlaceholderSimulation({ moduleId, title, onComplete, completed, testIdPrefix }) {
  const [playing, setPlaying] = useState(false);
  const [simProgress, setSimProgress] = useState(completed ? 100 : 0);
  const intervalRef = useRef(null);
  const SIM_DURATION_MS = 6000;

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const handlePlay = () => {
    if (completed) return;
    setPlaying(true);
    const startedAt = Date.now();
    intervalRef.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - startedAt) / SIM_DURATION_MS) * 100);
      setSimProgress(pct);
      if (pct >= 100) {
        clearTimer();
        setPlaying(false);
        onComplete && onComplete();
      }
    }, 100);
  };

  return (
    <div className="w-full">
      <div className="relative aspect-video w-full max-h-[80vh] rounded-lg overflow-hidden bg-slate-900 flex items-center justify-center group">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-black" />
        <div className="relative z-10 flex flex-col items-center gap-3 px-4 text-center">
          {completed ? (
            <CheckCircle2 className="h-14 w-14 text-emerald-400" data-testid={`${testIdPrefix}-completed-icon-${moduleId}`} />
          ) : (
            <button
              onClick={handlePlay}
              disabled={playing}
              data-testid={`${testIdPrefix}-play-button-${moduleId}`}
              className="h-16 w-16 rounded-full bg-white/10 border border-white/30 flex items-center justify-center hover:bg-denso-red hover:border-denso-red transition-all duration-200 disabled:opacity-70"
            >
              <Play className="h-7 w-7 text-white fill-white ml-0.5" />
            </button>
          )}
          <p className="text-white/70 text-xs uppercase tracking-[0.2em] font-semibold">
            {completed ? "Module Watched" : playing ? "Playing Video..." : "Video Placeholder"}
          </p>
          <p className="text-white text-sm font-medium">{title}</p>
        </div>
        {playing && (
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/10">
            <div
              className="h-full bg-denso-lightred transition-all duration-100"
              style={{ width: `${simProgress}%` }}
            />
          </div>
        )}
      </div>
      {!completed && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Real training video will appear here once added. Click play to simulate watching.
        </p>
      )}
    </div>
  );
}
